import React, { useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Braces, Download, Loader2, Upload } from "lucide-react";

import LabelValuePair from "../common/LabelValuePair";
import TablePagination from "../common/Table/TablePagination";
import RuleGroupList from "../ConflictRules/RuleGroupList";
import { AuditLog, AuditLogType, AuditLogTypeLabels } from "../../models/AuditLog";
import {
  ConflictRuleOutcome,
  conflictRulePagePath,
  LoadConflictRuleState,
} from "../../models/ConflictRule";
import { ConflictResolutionAction, ConflictResolutionActionLabels } from "../../models/ConflictResolutionAction";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import ConflictRulesRepository from "../../repositories/ConflictRulesRepository";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { dialogWide } from "@/lib/dialogSize";

/** The run's summary as the server stores it: PascalCase keys, enum values by their C# names. */
interface RunSummary {
  RuleName?: string | null;
  Reason?: string;
  Families?: string[];
  ConfirmedFileCount?: number;
  FilesPerAction?: { Action?: string; FileCount?: number }[];
  GroupsDecided?: number;
  GroupsSkipped?: number;
  CopiesThatWait?: number;
  Completed?: boolean;
  Stopped?: boolean;
}

/** An enum value stored by its C# name ("CasesMigration") as the GUI's camelCase value, when it is one. */
const fromStoredName = <T extends string>(values: T[], stored: string | undefined): T | null => {
  if (!stored) return null;
  const camel = (stored.charAt(0).toLowerCase() + stored.slice(1)) as T;
  return values.includes(camel) ? camel : null;
};

const familyLabel = (stored: string): string => {
  const family = fromStoredName(Object.values(MigrationType), stored);
  return family ? MigrationTypeLabels[family] : stored;
};

const actionLabel = (stored: string | undefined): string => {
  const action = fromStoredName(Object.values(ConflictResolutionAction), stored);
  return action ? ConflictResolutionActionLabels[action] : (stored ?? "—");
};

const count = (value: number | undefined): string => (typeof value === "number" ? value.toLocaleString() : "—");

const ALL_CHECKS = "all";

/**
 * Details of one conflict rule run: what it was asked to do and how far it got, then the groups it decided and the
 * groups it left alone, read page by page from the snapshot the run stored with its entry. Offers the rule itself,
 * the run's CSV, and loading the rule back into Resolve Conflicts by Rule.
 */
const ViewConflictRuleRunAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
  /** Admin or Editor with document access: may open the rule page and download the CSV. */
  canUseConflictRules?: boolean;
}> = ({ auditLog, open, onClose, organizationId, canUseConflictRules = false }) => {
  const repository = new ConflictRulesRepository();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<ConflictRuleOutcome>(ConflictRuleOutcome.Decided);
  const [check, setCheck] = useState<string>(ALL_CHECKS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [showRule, setShowRule] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const summary = (() => {
    if (!auditLog?.additionalData) return null;
    try {
      return JSON.parse(auditLog.additionalData) as RunSummary;
    } catch {
      return null;
    }
  })();

  const checkFilter = outcome === ConflictRuleOutcome.Skipped && check !== ALL_CHECKS ? check : null;
  const groupsQuery = useQuery({
    queryKey: [QUERY_KEYS.CONFLICT_RULE_RUN_GROUPS, organizationId, auditLog?.id, outcome, checkFilter, page, pageSize],
    enabled: open && !!auditLog?.id,
    queryFn: () => repository.getRunGroups(auditLog!.id, outcome, checkFilter, page + 1, pageSize),
    placeholderData: keepPreviousData,
  });
  const data = groupsQuery.data;
  const skippedTotal = data?.skipped.reduce((sum, skip) => sum + skip.groupCount, 0) ?? summary?.GroupsSkipped ?? 0;

  // Completed: every chunk applied. Stopped: it stopped part-way and the entry holds what had committed. Neither: the
  // entry was never amended, so its counts are what the run set out to do and its trail holds no decided files.
  const runState: "completed" | "stopped" | "unfinished" = summary?.Completed
    ? "completed"
    : summary?.Stopped
      ? "stopped"
      : "unfinished";

  const prettyRule = (() => {
    if (!data?.rule) return null;
    try {
      return JSON.stringify(JSON.parse(data.rule), null, 2);
    } catch {
      return data.rule;
    }
  })();

  const handleDownload = async () => {
    if (!auditLog) return;
    setDownloading(true);
    try {
      await repository.downloadRunCsv(auditLog.id);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const handleLoadRule = () => {
    if (!data?.rule) return;
    const state: LoadConflictRuleState = { conflictRule: data.rule };
    onClose();
    navigate(conflictRulePagePath(organizationId), { state });
  };

  const close = () => {
    setOutcome(ConflictRuleOutcome.Decided);
    setCheck(ALL_CHECKS);
    setPage(0);
    setShowRule(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className={`${dialogWide} overflow-y-auto`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[AuditLogType.ConflictRuleRun]}</DialogTitle>
            </DialogHeader>

            <Separator />

            {summary ? (
              <div className="flex min-w-0 flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-sm font-bold">Status:</span>
                    <Badge variant={runState === "completed" ? "success" : "warning"}>
                      {runState === "completed" ? "Completed" : runState === "stopped" ? "Stopped part-way" : "Incomplete"}
                    </Badge>
                  </div>
                  {runState === "stopped" && (
                    <p className="text-xs text-muted-foreground">
                      The run stopped before it finished. The counts and groups below are what it decided until then;
                      a new preview and run of the same rule decides the rest.
                    </p>
                  )}
                  {runState === "unfinished" && (
                    <p className="text-xs text-muted-foreground">
                      The run did not report how far it got. The counts below are what it set out to do, not what it
                      did, and no decided group is listed; check the entities for conflicts still open.
                    </p>
                  )}
                </div>

                <div className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                  <LabelValuePair label="Rule" value={summary.RuleName || "Unnamed rule"} />
                  <LabelValuePair label="Scope" value={(summary.Families ?? []).map(familyLabel).join(", ") || "—"} />
                  <LabelValuePair label="Files confirmed" value={count(summary.ConfirmedFileCount)} />
                  <LabelValuePair label="Groups decided" value={count(summary.GroupsDecided)} />
                  <LabelValuePair
                    label="Files decided"
                    value={
                      (summary.FilesPerAction ?? [])
                        .map((entry) => `${count(entry.FileCount)} ${actionLabel(entry.Action)}`)
                        .join(", ") || "None"
                    }
                  />
                  <LabelValuePair label="Copies set to wait for their winner" value={count(summary.CopiesThatWait)} />
                  <LabelValuePair label="Groups skipped" value={count(summary.GroupsSkipped)} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold">Reason:</span>
                  <span className="whitespace-pre-wrap break-words text-xs">{summary.Reason || "—"}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setShowRule(!showRule)}
                    disabled={!data?.rule}
                  >
                    <Braces className="size-4" />
                    {showRule ? "Hide rule" : "Show rule"}
                  </Button>
                  {canUseConflictRules && (
                    <>
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={handleDownload} disabled={downloading}>
                        {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                        Download CSV
                      </Button>
                      <Button size="sm" className="gap-1.5" onClick={handleLoadRule} disabled={!data?.rule}>
                        <Upload className="size-4" />
                        Load rule
                      </Button>
                    </>
                  )}
                </div>

                {showRule && prettyRule && (
                  <pre className="max-h-72 min-w-0 overflow-auto rounded-md border bg-muted p-3 font-mono text-xs">
                    {prettyRule}
                  </pre>
                )}

                <div className="flex min-w-0 flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={outcome}
                      onValueChange={(value) => {
                        if (!value) return;
                        setOutcome(value as ConflictRuleOutcome);
                        setCheck(ALL_CHECKS);
                        setPage(0);
                      }}
                    >
                      <ToggleGroupItem value={ConflictRuleOutcome.Decided}>
                        Decided ({count(data?.decidedGroupCount ?? summary.GroupsDecided)})
                      </ToggleGroupItem>
                      <ToggleGroupItem value={ConflictRuleOutcome.Skipped}>
                        Skipped ({count(skippedTotal)})
                      </ToggleGroupItem>
                    </ToggleGroup>
                    {outcome === ConflictRuleOutcome.Skipped && (data?.skipped.length ?? 0) > 0 && (
                      <Select
                        value={check}
                        onValueChange={(value) => {
                          setCheck(value);
                          setPage(0);
                        }}
                      >
                        <SelectTrigger size="sm" className="w-80 max-w-full" aria-label="Why the groups were skipped">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL_CHECKS}>Every reason</SelectItem>
                          {data!.skipped.map((skip) => (
                            <SelectItem key={skip.check} value={skip.check}>
                              {skip.label} ({skip.groupCount.toLocaleString()})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {groupsQuery.isLoading && (
                    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading the run's groups…
                    </div>
                  )}
                  {groupsQuery.isError && (
                    <p className="py-4 text-sm text-muted-foreground">Failed to load the run's groups.</p>
                  )}
                  {data && (
                    <>
                      {data.groups.length === 0 ? (
                        <p className="py-2 text-sm text-muted-foreground">
                          {outcome === ConflictRuleOutcome.Decided ? "No group was decided." : "No group was skipped."}
                        </p>
                      ) : (
                        <div className="max-h-[28rem] min-w-0 overflow-y-auto pr-1">
                          <RuleGroupList groups={data.groups} />
                        </div>
                      )}
                      {data.totalCount > 20 && (
                        <TablePagination
                          page={page}
                          rowsPerPage={pageSize}
                          rows={data.totalCount}
                          onPageChange={setPage}
                          onRowsPerPageChange={(value) => {
                            setPageSize(value);
                            setPage(0);
                          }}
                          rowsPerPageOptions={[20, 50, 100]}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-sm">Failed to parse additionalData</span>
            )}

            <Separator />

            <LabelValuePair label="Performed By" value={auditLog.performerEmailAddress} />
            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewConflictRuleRunAuditLogDetailsModal;
